import { useCallback, useEffect, useState } from 'react';
import { PUBLIC_PATHS, isPublicSitePath, publicViewForPath } from '../navigation/publicRoutes';

const readLocation = () => {
  if (typeof window === 'undefined') return { view: 'landing', homeId: null, section: null };
  return {
    ...publicViewForPath(window.location.pathname),
    section: window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : null,
  };
};

// Views that may keep an unrelated deep link in the address bar (see below).
const AUTH_VIEWS = new Set(['login', 'register']);

// Public paths that also stay put on the way to sign-in: App.js opens Post
// Listing after sign-in when the visitor came from /sell.
const KEEP_ON_SIGN_IN = new Set(['/sell']);

const keepsPathOnSignIn = (pathname) => {
  const normalized = String(pathname || '').replace(/\/+$/, '') || '/';
  return !isPublicSitePath(pathname) || KEEP_ON_SIGN_IN.has(normalized);
};

/**
 * The signed-out view, kept in step with the address bar.
 *
 * - `go(path)` moves to a public path ("/pricing", "/#faq", "/homes/4") and
 *   pushes a history entry, so Back returns to the previous public page.
 * - `show(view)` switches to a view with no path of its own
 *   ('forgot-password', 'reset-password'), or to login/register.
 * - Back/Forward (popstate) re-reads the path.
 *
 * A deep link into the app (e.g. /documents/12/signed) is never rewritten on
 * the way to sign-in or sign-up, so the path-based route still renders after
 * the visitor signs in. Going to a public page from there does rewrite it:
 * they have left the deep link.
 *
 * While the public site is off (`enabled` false, see usePublicSiteEnabled),
 * `show()` only switches the view and never touches the address bar, as
 * sign-in / sign-up / start-listing did before the public site existed.
 *
 * @param {string|null} [initialView] - Overrides the path's view on first render.
 * @param {boolean} [enabled=true] - Whether the public site is on.
 * @returns {{route: {view: string, homeId: number|null, section: string|null, nonce: number},
 *   go: (path: string) => void, show: (view: string) => void}}
 */
export default function usePublicRoute(initialView = null, enabled = true) {
  const [route, setRoute] = useState(() => {
    const fromPath = readLocation();
    return { ...fromPath, view: initialView || fromPath.view, nonce: 0 };
  });

  useEffect(() => {
    const onPopState = () => setRoute((prev) => ({ ...readLocation(), nonce: prev.nonce + 1 }));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const go = useCallback((path) => {
    const url = new URL(path, window.location.origin);
    const next = publicViewForPath(url.pathname);
    const keepDeepLink = AUTH_VIEWS.has(next.view) && keepsPathOnSignIn(window.location.pathname);
    const target = `${url.pathname}${url.hash}`;
    if (!keepDeepLink && target !== `${window.location.pathname}${window.location.hash}`) {
      window.history.pushState({}, '', target);
    }
    setRoute((prev) => ({
      ...next,
      section: url.hash ? decodeURIComponent(url.hash.slice(1)) : null,
      nonce: prev.nonce + 1,
    }));
  }, []);

  const show = useCallback((view) => {
    if (enabled && PUBLIC_PATHS[view]) {
      go(PUBLIC_PATHS[view]);
      return;
    }
    setRoute((prev) => ({ ...prev, view, section: null, nonce: prev.nonce + 1 }));
  }, [go, enabled]);

  return { route, go, show };
}
